import CountrySelector from '@/components/common/CountrySelector';
import { apiUrls, services, env } from '@/constant/configure';
import sodomallLogo from '@/assets/images/logo/sodomall_logo.png';
import { dialogOpen } from '@/modules/MuiDialog';
import LoginDialog from '@/pages/dialogs/LoginDialog';
import { checkMallSession, signOutSession } from '@/services/auth.service';
import { useCurrentMallStore, useSessionStore, useSpaceUserV2Store, useUserStore } from '@/store';
import { useLanguage } from '@/store/modules/languageStore';
import sessionStore from '@/store/modules/sessionStore';
import { broadcastLogout } from '@/utils/authBroadcast';
import { isSodomallPartnerDomain } from '@/utils/checkDomain';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import InventoryIcon from '@mui/icons-material/Inventory';
import {
    AppBar,
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface DDMNavBarProps {
    setAppBarHeight?: (height: number) => void;
}

const DDMNavBar = ({ setAppBarHeight }: DDMNavBarProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { nickname, image_url } = useUserStore();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('KR');
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
    const appBarRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();
    const { userId, mallId, accessToken } = useSessionStore();
    const { mallPath } = useCurrentMallStore();
    const { path: spacePath, grade_name } = useSpaceUserV2Store();
    const { getCurrentCountryCode } = useLanguage();
    const isUserLoggedIn = !!(accessToken && userId);

    // 소도몰 역할인지 확인
    const isSodomall = grade_name === '소도몰';

    // mallPath가 없으면 spacePath를 사용
    const effectiveMallPath = mallPath || spacePath;

    // 프로필링크 메뉴(/studio)와 쇼핑몰 관리 메뉴(/mall)에서는 좌우 여백 제거
    const isFullWidthLayout = location.pathname.startsWith('/studio') || location.pathname.startsWith('/mall');

    // AppBar 높이 설정
    useEffect(() => {
        if (appBarRef.current && setAppBarHeight) {
            setAppBarHeight(appBarRef.current.clientHeight);
        }
    }, [setAppBarHeight]);

    // 언어 스토어에서 현재 국가 코드 가져오기
    useEffect(() => {
        const currentCountryCode = getCurrentCountryCode();
        setSelectedCountry(currentCountryCode);
    }, [getCurrentCountryCode]);

    // 국가 변경 핸들러
    const handleCountryChange = (countryCode: string) => {
        setSelectedCountry(countryCode);
        // CountrySelector에서 이미 언어 스토어를 업데이트하므로 여기서는 상태만 동기화
    };

    // 사용자 메뉴 핸들러
    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    // 로그아웃 핸들러
    const handleLogout = async () => {
        // 메뉴를 먼저 닫아서 anchorEl이 유효한 상태에서 처리
        handleUserMenuClose();

        // 메뉴가 완전히 닫힐 때까지 충분한 시간 대기 (트랜지션 포함)
        await new Promise((resolve) => setTimeout(resolve, 150));

        // 다른 탭들에게 로그아웃 메시지 브로드캐스트
        broadcastLogout();

        await signOutSession();
        toast.success(t('auth.logoutSuccess'));

        // 토스트 메시지가 표시된 후 리다이렉트
        setTimeout(() => {
            // 소도몰 파트너 도메인인 경우 로그인 페이지로 이동
            if (isSodomallPartnerDomain()) {
                window.location.href = '/login';
            } else {
                window.location.reload();
            }
        }, 100);
    };

    const menuItems: any[] = [
        // { label: '기획전신상', path: '/' },
        // { label: '랭킹', path: '/', badge: 'NEW' },
        // { label: 'DETAILS', path: '/' },
        // { label: '트렌드', path: '/' },
        // { label: 'SALE', path: '/' },
    ];

    return (
        <>
            <Box sx={{ position: 'relative' }}>
                <AppBar
                    ref={appBarRef}
                    position="sticky"
                    elevation={0}
                    sx={{
                        backgroundColor: '#fff',
                        zIndex: 'calc( var(--v-zIndex-drawer) + 1)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        borderBottom: '1px solid #e0e0e0',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: isFullWidthLayout ? 'none' : 2000,
                            margin: isFullWidthLayout ? 0 : '0 auto',
                            width: '100%',
                            px: { xs: 0, sm: 2, md: '40px', lg: '40px' },
                            py: { xs: 1, md: 1.5 },
                        }}
                    >
                        <Toolbar
                            disableGutters
                            sx={{
                                minHeight: { xs: 55, md: 62 },
                                justifyContent: 'space-between',
                                px: { xs: 2, sm: 0 },
                            }}
                        >
                            {/* 좌측 영역: 로고 */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* 모바일 메뉴 버튼 */}
                                {isMobile && (
                                    <IconButton
                                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                        sx={{ color: '#222' }}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                )}

                                {/* 로고 */}
                                <Box
                                    component={Link}
                                    to={isSodomall ? `/mall/${mallId}` : 'https://ddmlive.io/'}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        textDecoration: 'none',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={
                                            isSodomall
                                                ? sodomallLogo
                                                : 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/ddm_live_logo.png'
                                        }
                                        alt={isSodomall ? 'Sodomall' : 'DDM Live'}
                                        sx={{
                                            height: isSodomall ? { xs: 32, md: 52 } : { xs: 24, md: 40 },
                                            width: 'auto',
                                            mb: 2,
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* 프로필링크 + 몰 관리 버튼 (왼쪽) */}
                            {!isMobile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 3 }}>
                                    {isUserLoggedIn ? (
                                        <>
                                            {/* 몰 관리 */}
                                            <Button
                                                variant={
                                                    location.pathname.startsWith('/mall/') ? 'contained' : 'outlined'
                                                }
                                                color={isSodomall ? 'info' : 'deeper-orange'}
                                                onClick={async () => {
                                                    // 실제 세션 검증 후 처리 (오래된 세션 방지)
                                                    const session = sessionStore.getState();

                                                    // localStorage에 세션이 있어도 실제 유효성 검증
                                                    if (session.accessToken && session.userId) {
                                                        try {
                                                            // 실제 세션 검증
                                                            const isValidSession = await checkMallSession();
                                                            if (isValidSession) {
                                                                // 검증 성공 시 최신 세션 정보로 이동
                                                                const currentSession = sessionStore.getState();
                                                                if (currentSession.mallId) {
                                                                    navigate(`/mall/${currentSession.mallId}`);
                                                                } else {
                                                                    navigate('/switch');
                                                                }
                                                                return;
                                                            }
                                                        } catch (error) {
                                                            console.error('세션 검증 실패:', error);
                                                        }
                                                    }

                                                    // 세션이 없거나 검증 실패 시 로그인 팝업 표시
                                                    dialogOpen({
                                                        innerElement: (
                                                            <LoginDialog
                                                                onLoginSuccess={() => {
                                                                    const currentSession = sessionStore.getState();
                                                                    if (currentSession.mallId) {
                                                                        navigate(`/mall/${currentSession.mallId}`);
                                                                    } else {
                                                                        navigate('/switch');
                                                                    }
                                                                }}
                                                            />
                                                        ),
                                                        maxWidth: false,
                                                        sx: {
                                                            '& .MuiDialog-paper': {
                                                                maxWidth: '420px',
                                                                width: '100%',
                                                            },
                                                        },
                                                    });
                                                }}
                                                sx={{
                                                    borderRadius: '20px',
                                                }}
                                            >
                                                {t('mall.mallManagement')}
                                            </Button>
                                            {/* 프로필링크 */}
                                            <Button
                                                onClick={async () => {
                                                    // 실제 세션 검증 후 처리 (오래된 세션 방지)
                                                    const session = sessionStore.getState();

                                                    // localStorage에 세션이 있어도 실제 유효성 검증
                                                    if (session.accessToken && session.userId) {
                                                        try {
                                                            // 실제 세션 검증
                                                            const isValidSession = await checkMallSession();
                                                            if (isValidSession) {
                                                                // 검증 성공 시 최신 세션 정보로 이동
                                                                const currentSession = sessionStore.getState();
                                                                navigate(`/studio/${currentSession.userId}`);
                                                                return;
                                                            }
                                                        } catch (error) {
                                                            console.error('세션 검증 실패:', error);
                                                        }
                                                    }

                                                    // 세션이 없거나 검증 실패 시 로그인 팝업 표시
                                                    dialogOpen({
                                                        innerElement: (
                                                            <LoginDialog
                                                                onLoginSuccess={() => {
                                                                    const currentSession = sessionStore.getState();
                                                                    navigate(`/studio/${currentSession.userId}`);
                                                                }}
                                                            />
                                                        ),
                                                        maxWidth: false,
                                                        sx: {
                                                            '& .MuiDialog-paper': {
                                                                maxWidth: '420px',
                                                                width: '100%',
                                                            },
                                                        },
                                                    });
                                                }}
                                                color={isSodomall ? 'info' : 'deeper-orange'}
                                                variant={
                                                    location.pathname === `/studio/${userId}` ||
                                                    (location.pathname.startsWith(`/studio/${userId}/`) &&
                                                        !location.pathname.includes('/live/'))
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                sx={{
                                                    borderRadius: '20px',
                                                }}
                                            >
                                                {t('studio.profileLinkManagement')}
                                            </Button>

                                            {/* 라이브방송 - 소도몰 역할일 때 숨김 */}
                                            {!isSodomall && (
                                                <Button
                                                    onClick={async () => {
                                                        // 실제 세션 검증 후 처리 (오래된 세션 방지)
                                                        const session = sessionStore.getState();

                                                        // localStorage에 세션이 있어도 실제 유효성 검증
                                                        if (session.accessToken && session.userId) {
                                                            try {
                                                                // 실제 세션 검증
                                                                const isValidSession = await checkMallSession();
                                                                if (isValidSession) {
                                                                    // 검증 성공 시 최신 세션 정보로 이동
                                                                    const currentSession = sessionStore.getState();
                                                                    navigate(
                                                                        `/studio/${currentSession.userId || '-'}/live/schedule-reg`,
                                                                    );
                                                                    return;
                                                                }
                                                            } catch (error) {
                                                                console.error('세션 검증 실패:', error);
                                                            }
                                                        }

                                                        // 세션이 없거나 검증 실패 시 로그인 팝업 표시
                                                        dialogOpen({
                                                            innerElement: (
                                                                <LoginDialog
                                                                    onLoginSuccess={() => {
                                                                        const currentSession = sessionStore.getState();
                                                                        navigate(
                                                                            `/studio/${currentSession.userId || '-'}/live/schedule-reg`,
                                                                        );
                                                                    }}
                                                                />
                                                            ),
                                                            maxWidth: false,
                                                            sx: {
                                                                '& .MuiDialog-paper': {
                                                                    maxWidth: '420px',
                                                                    width: '100%',
                                                                },
                                                            },
                                                        });
                                                    }}
                                                    sx={{
                                                        borderRadius: '20px',
                                                    }}
                                                    color="deeper-orange"
                                                    variant={
                                                        location.pathname.includes('/live/') ? 'contained' : 'outlined'
                                                    }
                                                >
                                                    {t('studio.liveBroadcast')}
                                                </Button>
                                            )}

                                            {/* 상품 - 소도몰 역할일 때 숨김 */}
                                            {!isSodomall && (
                                                <Button
                                                    onClick={async () => {
                                                        navigate('/');
                                                    }}
                                                    sx={{
                                                        borderRadius: '20px',
                                                    }}
                                                    color="deeper-orange"
                                                    variant={location.pathname === '/' ? 'contained' : 'outlined'}
                                                >
                                                    {t('navigation.products')}
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        /* 구경하기 */
                                        <Button
                                            startIcon={
                                                <Box
                                                    component={'img'}
                                                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202511/d0a409620b923dfcdf8643941e299432.png"
                                                    sx={{
                                                        width: '24px',
                                                        height: '24px',
                                                    }}
                                                />
                                            } // 아이콘 경로 수정
                                            onClick={() => {
                                                navigate('/');
                                            }}
                                            sx={{
                                                borderRadius: '20px',
                                                backgroundColor: '#FFF8F3',
                                            }}
                                            color="deeper-orange"
                                            variant="outlined"
                                        >
                                            {t('navigation.browse')}
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {/* 스페이서 */}
                            <Box sx={{ flex: 1 }} />

                            {/* 우측 아이콘 */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, md: 0.5 } }}>
                                {/* <IconButton size="small" sx={{ color: '#666', p: 0.8 }}>
                                <SearchIcon sx={{ fontSize: 24 }} />
                            </IconButton> */}

                                {/* {!isMobile && (
                                <>
                                    <IconButton size="small" sx={{ color: '#666', p: 0.8 }}>
                                        <ChatBubbleOutlineIcon sx={{ fontSize: 24 }} />
                                    </IconButton>
                                    <IconButton size="small" sx={{ color: '#666', p: 0.8 }}>
                                        <FavoriteBorderIcon sx={{ fontSize: 24 }} />
                                    </IconButton>
                                    <IconButton size="small" sx={{ color: '#666', p: 0.8 }}>
                                        <BookmarkBorderIcon sx={{ fontSize: 24 }} />
                                    </IconButton>
                                </>
                            )} */}

                                {/* <IconButton size="small" sx={{ color: '#666', p: 0.8 }}>
                                <Badge
                                    badgeContent={cartCount}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: '#FF0033',
                                            color: '#fff',
                                            fontSize: 9,
                                            fontWeight: 700,
                                            minWidth: 14,
                                            height: 14,
                                            padding: '0 3px',
                                        },
                                    }}
                                >
                                    <ShoppingCartOutlinedIcon sx={{ fontSize: 24 }} />
                                </Badge>
                            </IconButton> */}

                                {/* 소도몰 사용자 전용: 판매링크 바로가기 버튼 (메뉴 외부) */}
                                {isUserLoggedIn && isSodomall && (
                                    <Button
                                        variant="contained"
                                        color="info"
                                        startIcon={<LanguageIcon />}
                                        onClick={() => {
                                            if (effectiveMallPath) {
                                                const baseUrl =
                                                    env !== 'prd'
                                                        ? 'https://sodomall.dev.flowing.space'
                                                        : 'https://sodomall.store';
                                                const url = `${baseUrl}/${effectiveMallPath}`;
                                                window.open(url, '_blank');
                                            }
                                        }}
                                        sx={{
                                            borderRadius: '20px',
                                            mr: 1.5,
                                            px: 2,
                                            py: 0.8,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        판매링크 바로가기
                                    </Button>
                                )}

                                {/* 사용자 프로필 - 항상 표시 */}
                                {isUserLoggedIn ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            cursor: 'pointer',
                                            px: 2,
                                            py: 0.8,
                                            minWidth: { xs: 'auto', md: 160 },
                                            backgroundColor: '#f8f8f8',
                                            borderRadius: '8px',
                                            border: '1px solid #e0e0e0',
                                            transition: 'all 0.1s ease',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                                borderColor: '#d0d0d0',
                                            },
                                        }}
                                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                                            handleUserMenuOpen(event);
                                        }}
                                    >
                                        <Avatar
                                            src={image_url || undefined}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                bgcolor: '#666',
                                                fontSize: 12,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {!image_url && (nickname?.[0]?.toUpperCase() || 'U')}
                                        </Avatar>
                                        {!isMobile && (
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: '#222',
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {nickname || '사용자'}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Button
                                        onClick={() => {
                                            dialogOpen({
                                                innerElement: <LoginDialog />,
                                                maxWidth: false,
                                                sx: {
                                                    '& .MuiDialog-paper': {
                                                        maxWidth: '420px',
                                                        width: '100%',
                                                    },
                                                },
                                            });
                                        }}
                                        sx={{
                                            color: '#222',
                                            fontSize: 14,
                                            fontWeight: 500,
                                            px: 2,
                                            py: 0.8,
                                            textTransform: 'none',
                                            border: '1px solid #E0E0E0',
                                            borderRadius: '8px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                borderColor: '#999',
                                            },
                                        }}
                                    >
                                        {t('auth.login')}
                                    </Button>
                                )}

                                {/* 사용자 드롭다운 메뉴 */}
                                <Menu
                                    anchorEl={userMenuAnchor}
                                    open={Boolean(userMenuAnchor)}
                                    onClose={handleUserMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    disableAutoFocusItem
                                    disableEnforceFocus
                                    disableRestoreFocus
                                    sx={{
                                        '& .MuiPaper-root': {
                                            mt: 1.5,
                                            minWidth: 260,
                                            borderRadius: '12px',
                                            boxShadow:
                                                '0 8px 32px rgba(251, 109, 72, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
                                            border: '1px solid rgba(251, 109, 72, 0.1)',
                                            overflow: 'hidden',
                                        },
                                        '& .MuiList-root': {
                                            py: 1.5,
                                        },
                                        '& .MuiMenuItem-root': {
                                            px: 2.5,
                                            py: 1.5,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: '4px',
                                                background:
                                                    'linear-gradient(180deg, #FF9A7A 0%, #FB6D48 50%, #E55A3B 100%)',
                                                opacity: 0,
                                                transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            },
                                            '&:hover': {
                                                backgroundColor: 'rgba(251, 109, 72, 0.08)',
                                                '&::before': {
                                                    opacity: 1,
                                                },
                                                '& .MuiListItemIcon-root': {
                                                    background: 'linear-gradient(135deg, #FF9A7A 0%, #FB6D48 100%)',
                                                },
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(251, 109, 72, 0.12)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(251, 109, 72, 0.16)',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {/* 프로필링크 바로가기 - 소도몰 역할일 때 숨김 (외부 버튼으로 대체) */}
                                    {!isSodomall && (
                                        <MenuItem
                                            onClick={() => {
                                                if (effectiveMallPath) {
                                                    window.open(
                                                        `${apiUrls.spacehome_url}/${effectiveMallPath}`,
                                                        '_blank',
                                                    );
                                                }
                                                handleUserMenuClose();
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 56,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start',
                                                    mr: 1.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: 'linear-gradient(135deg, #FB6D48 0%, #E55A3B 100%)',
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        boxShadow: '0 4px 12px rgba(251, 109, 72, 0.3)',
                                                    }}
                                                >
                                                    <LanguageIcon sx={{ fontSize: 20, color: '#fff' }} />
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={t('studio.profileLinkDirectAccess')}
                                                sx={{
                                                    '& .MuiTypography-root': {
                                                        color: '#333',
                                                        fontWeight: 500,
                                                        fontSize: '15px',
                                                        letterSpacing: '-0.2px',
                                                    },
                                                }}
                                            />
                                        </MenuItem>
                                    )}
                                    {/* 쇼핑몰 바로가기 - 소도몰 역할일 때 숨김 */}
                                    {!isSodomall && (
                                        <MenuItem
                                            onClick={() => {
                                                if (effectiveMallPath) {
                                                    window.open(
                                                        `${services.flowing_mall_url}/${effectiveMallPath}`,
                                                        '_blank',
                                                    );
                                                } else {
                                                    navigate('/switch');
                                                }
                                                handleUserMenuClose();
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 56,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start',
                                                    mr: 1.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: 'linear-gradient(135deg, #FB6D48 0%, #E55A3B 100%)',
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        boxShadow: '0 4px 12px rgba(251, 109, 72, 0.3)',
                                                    }}
                                                >
                                                    <StorefrontOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={t('mall.mallDirectAccess')}
                                                sx={{
                                                    '& .MuiTypography-root': {
                                                        color: '#333',
                                                        fontWeight: 500,
                                                        fontSize: '15px',
                                                        letterSpacing: '-0.2px',
                                                    },
                                                }}
                                            />
                                        </MenuItem>
                                    )}
                                    <Divider
                                        sx={{
                                            my: 1.5,
                                            mx: 2,
                                            background:
                                                'linear-gradient(90deg, transparent 0%, rgba(251, 109, 72, 0.2) 50%, transparent 100%)',
                                            height: '1px',
                                            border: 'none',
                                        }}
                                    />
                                    <MenuItem
                                        onClick={() => {
                                            navigate(`/studio/${userId}/user-setting`);
                                            handleUserMenuClose();
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 56,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                mr: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '10px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 'linear-gradient(135deg, #FB6D48 0%, #E55A3B 100%)',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: '0 4px 12px rgba(251, 109, 72, 0.3)',
                                                }}
                                            >
                                                <SettingsIcon sx={{ fontSize: 20, color: '#fff' }} />
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={t('navigation.accountSettings')}
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    color: '#333',
                                                    fontWeight: 500,
                                                    fontSize: '15px',
                                                    letterSpacing: '-0.2px',
                                                },
                                            }}
                                        />
                                    </MenuItem>
                                    <Divider
                                        sx={{
                                            my: 1.5,
                                            mx: 2,
                                            background:
                                                'linear-gradient(90deg, transparent 0%, rgba(251, 109, 72, 0.2) 50%, transparent 100%)',
                                            height: '1px',
                                            border: 'none',
                                        }}
                                    />
                                    <MenuItem onClick={handleLogout}>
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 56,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                mr: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '10px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 'linear-gradient(135deg, #FB6D48 0%, #E55A3B 100%)',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    boxShadow: '0 4px 12px rgba(251, 109, 72, 0.3)',
                                                }}
                                            >
                                                <LogoutIcon sx={{ fontSize: 20, color: '#fff' }} />
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={t('auth.logout')}
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    color: '#333',
                                                    fontWeight: 500,
                                                    fontSize: '15px',
                                                    letterSpacing: '-0.2px',
                                                },
                                            }}
                                        />
                                    </MenuItem>
                                </Menu>

                                {/* 국가 선택 UI - 맨 오른쪽에 배치 (소도몰 역할일 때 숨김) */}
                                {!isMobile && !isSodomall && (
                                    <CountrySelector
                                        selectedCountry={selectedCountry}
                                        onCountryChange={handleCountryChange}
                                        sx={{ ml: 1 }}
                                    />
                                )}
                            </Box>
                        </Toolbar>
                    </Box>
                </AppBar>
            </Box>

            {/* 모바일 메뉴 드로어 */}
            {isMobile && mobileMenuOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 60,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: '#fff',
                        zIndex: 1200,
                        overflowY: 'auto',
                        borderTop: '1px solid #e0e0e0',
                    }}
                >
                    <Box sx={{ p: 2 }}>
                        {/* 모바일 국가 선택 UI (소도몰 역할일 때 숨김) */}
                        {!isSodomall && (
                            <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #f0f0f0' }}>
                                <CountrySelector
                                    selectedCountry={selectedCountry}
                                    onCountryChange={handleCountryChange}
                                />
                            </Box>
                        )}

                        {isUserLoggedIn ? (
                            <>
                                {/* 몰 관리 */}
                                <Button
                                    fullWidth
                                    onClick={async () => {
                                        setMobileMenuOpen(false);

                                        const session = sessionStore.getState();

                                        if (session.accessToken && session.userId) {
                                            try {
                                                const isValidSession = await checkMallSession();
                                                if (isValidSession) {
                                                    const currentSession = sessionStore.getState();
                                                    if (currentSession.mallId) {
                                                        navigate(`/mall/${currentSession.mallId}`);
                                                    } else {
                                                        navigate('/switch');
                                                    }
                                                    return;
                                                }
                                            } catch (error) {
                                                console.error('세션 검증 실패:', error);
                                            }
                                        }

                                        dialogOpen({
                                            innerElement: (
                                                <LoginDialog
                                                    onLoginSuccess={() => {
                                                        const currentSession = sessionStore.getState();
                                                        if (currentSession.mallId) {
                                                            navigate(`/mall/${currentSession.mallId}`);
                                                        } else {
                                                            navigate('/switch');
                                                        }
                                                    }}
                                                />
                                            ),
                                            maxWidth: false,
                                            sx: {
                                                '& .MuiDialog-paper': {
                                                    maxWidth: '420px',
                                                    width: '100%',
                                                },
                                            },
                                        });
                                    }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        color: '#222',
                                        fontSize: 15,
                                        fontWeight: 500,
                                        py: 2,
                                        borderBottom: '1px solid #f0f0f0',
                                        textTransform: 'none',
                                    }}
                                >
                                    {t('mall.mallManagement')}
                                </Button>

                                {/* 프로필링크 */}
                                <Button
                                    fullWidth
                                    onClick={async () => {
                                        setMobileMenuOpen(false);

                                        const session = sessionStore.getState();

                                        if (session.accessToken && session.userId) {
                                            try {
                                                const isValidSession = await checkMallSession();
                                                if (isValidSession) {
                                                    navigate(`/studio/${userId}`);
                                                    return;
                                                }
                                            } catch (error) {
                                                console.error('세션 검증 실패:', error);
                                            }
                                        }

                                        dialogOpen({
                                            innerElement: (
                                                <LoginDialog
                                                    onLoginSuccess={() => {
                                                        navigate(`/studio/${userId}`);
                                                    }}
                                                />
                                            ),
                                            maxWidth: false,
                                            sx: {
                                                '& .MuiDialog-paper': {
                                                    maxWidth: '420px',
                                                    width: '100%',
                                                },
                                            },
                                        });
                                    }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        color: '#222',
                                        fontSize: 15,
                                        fontWeight: 500,
                                        py: 2,
                                        borderBottom: '1px solid #f0f0f0',
                                        textTransform: 'none',
                                    }}
                                >
                                    {t('studio.profileLinkManagement')}
                                </Button>

                                {/* 라이브방송 - 소도몰 역할일 때 숨김 */}
                                {!isSodomall && (
                                    <Button
                                        fullWidth
                                        onClick={async () => {
                                            setMobileMenuOpen(false);

                                            const session = sessionStore.getState();

                                            if (session.accessToken && session.userId) {
                                                try {
                                                    const isValidSession = await checkMallSession();
                                                    if (isValidSession) {
                                                        const currentSession = sessionStore.getState();
                                                        navigate(
                                                            `/studio/${currentSession.userId || '-'}/live/schedule-reg`,
                                                        );
                                                        return;
                                                    }
                                                } catch (error) {
                                                    console.error('세션 검증 실패:', error);
                                                }
                                            }

                                            dialogOpen({
                                                innerElement: (
                                                    <LoginDialog
                                                        onLoginSuccess={() => {
                                                            const currentSession = sessionStore.getState();
                                                            navigate(
                                                                `/studio/${currentSession.userId || '-'}/live/schedule-reg`,
                                                            );
                                                        }}
                                                    />
                                                ),
                                                maxWidth: false,
                                                sx: {
                                                    '& .MuiDialog-paper': {
                                                        maxWidth: '420px',
                                                        width: '100%',
                                                    },
                                                },
                                            });
                                        }}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            color: '#222',
                                            fontSize: 15,
                                            fontWeight: 500,
                                            py: 2,
                                            borderBottom: '1px solid #f0f0f0',
                                            textTransform: 'none',
                                        }}
                                    >
                                        {t('studio.liveBroadcast')}
                                    </Button>
                                )}

                                {/* 상품 - 소도몰 역할일 때 숨김 */}
                                {!isSodomall && (
                                    <Button
                                        fullWidth
                                        onClick={async () => {
                                            navigate('/');
                                        }}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            color: '#222',
                                            fontSize: 15,
                                            fontWeight: 500,
                                            py: 2,
                                            borderBottom: '1px solid #f0f0f0',
                                            textTransform: 'none',
                                        }}
                                    >
                                        {t('navigation.products')}
                                    </Button>
                                )}
                            </>
                        ) : (
                            /* 구경하기 */
                            <Button
                                fullWidth
                                onClick={() => {
                                    setMobileMenuOpen(false);
                                    navigate('/');
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    color: '#222',
                                    fontSize: 15,
                                    fontWeight: 500,
                                    py: 2,
                                    borderBottom: '1px solid #f0f0f0',
                                    textTransform: 'none',
                                }}
                            >
                                {t('navigation.browseNewArrivals')}
                            </Button>
                        )}
                    </Box>
                </Box>
            )}
        </>
    );
};

export default DDMNavBar;
